import { AfterViewInit, Directive, ElementRef, Injectable, OnInit, Pipe, PipeTransform } from '@angular/core';
import { ToasterService } from './toaster/toaster.service';
import { md5 } from './md5.service';
import { localStorageService } from './localstorage.service';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  Observable,
  Subject,
} from 'rxjs';
import moment from 'moment';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { MainService } from './main.service';
import { RouteAuthService } from './route-auth.service';
import { HttpParameterCodec, HttpParams } from '@angular/common/http';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import * as ExcelJS from "exceljs";
import { ApiService } from './api.service';
import { email_regex } from './regex';
import { PhoneFormatService } from './phone-format.service';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  ID_ALPHA_ENCODING_PREFIX,
  ID_ENCODING_PREFIX,
  ID_ENCODING_RADIX,
  isEncodedId,
  isUnencodedId,
} from './id-encoding';
@Injectable({
  providedIn: 'root',
})
export class SharedService {
  public timezone: string = '';
  public timezoneName: string = '';
  public selected_country$: BehaviorSubject<any> = new BehaviorSubject({});
  private key_sort_by = '';
  public mask = '';
  public noteadded: boolean = false;
  public updated_timezone$: BehaviorSubject<any> = new BehaviorSubject({});

  public timezone$: BehaviorSubject<any> = new BehaviorSubject('-800');
  public timezoneName$: BehaviorSubject<any> = new BehaviorSubject('');
  public zone$: BehaviorSubject<any> = new BehaviorSubject('');
  // Communication subtab to open on the debt recovery details page (set from notification redirects)
  public commsRedirect$: BehaviorSubject<{ split_req_id: any, subtab: string } | null> = new BehaviorSubject<{ split_req_id: any, subtab: string } | null>(null);
  public isDummy$: BehaviorSubject<any> = new BehaviorSubject([]);
  public paymentSourceType = {
    CARD: 3,
    DEBIT_CARD: 2,
    BANK: 1,
    WALLET:4
  };
  timeoutError: any;

  private _notificationCount = new BehaviorSubject<number>(0);
  notificationCount$ = this._notificationCount.asObservable();
  
  audio: HTMLAudioElement = new Audio('assets/audio/notify.mp3');
  private readonly idEncodingPrefix = ID_ENCODING_PREFIX;
  private readonly idAlphaEncodingPrefix = ID_ALPHA_ENCODING_PREFIX;
  private readonly idEncodingRadix = ID_ENCODING_RADIX;
  // The XOR result is a uint32, i.e. at most 7 base36 chars. Adding the offset below keeps it at
  // exactly 7 (36^6 <= value < 36^7) so the body never opens with a run of padding zeros.
  private readonly idEncodedValueLength = 7;
  private readonly idEncodedValueOffset = Math.pow(36, 6);
  // Bijective bit mix - multiply by an odd constant mod 2^32, undone by its modular inverse
  // (idScrambleFactor * idScrambleInverse === 1 mod 2^32). XOR alone only perturbs the low bits,
  // so consecutive IDs would otherwise encode to strings sharing a long visible prefix.
  private readonly idScrambleFactor = 2654435761;
  private readonly idScrambleInverse = 244002641;
  private get idXorKey(): number {
    return environment.id_encoding_key || 58437291;
  }
  // Total length of the encoded string (prefix + body). Kept fixed-width so encoded IDs
  // don't leak the magnitude of the original numeric ID via string length.
  private get encodedIdLength(): number {
    return environment.id_encoding_length || 15;
  }
  constructor(
    private _toaster: ToasterService,
    private _local: localStorageService,
    private _main: MainService,
    public routerAuth: RouteAuthService,
    private _api: ApiService,
    private router: Router,
    private location: Location
  ) {
    this.device_id();
  }

  modalTypes = {
    pictureModal: 'pictureModal',
    CallModal: "CallModal",
  };

  customPopups(message: string, flag: Number) {
    if (flag == 1) {
      this._toaster.showToaster('Error', message, 'error');
    } else if (flag == 2) {
      this._toaster.showToaster('Warning', message, 'warning');
    } else {
      this._toaster.showToaster('Success', message, 'success');
    }
  }

  crypto(tokenFromUI: string) {
    return md5(tokenFromUI);
  }

  updateMasking(format: '') {
    return format.replace(/#/g, '0');
  }

  guid() {
    const a = new Date();
    const nav = window.navigator;
    const screen = window.screen;
    let guid = nav.mimeTypes.length.toString();
    guid += nav.userAgent.replace(/\D+/g, '');
    guid += nav.plugins.length;
    guid += screen.height || '';
    guid += screen.width || '';
    guid += screen.pixelDepth || '';
    guid += a.getTime();
    return guid;
  }

  device_id() {
    if (!this._local.customGetItem('user')) {
      const guidLog = this.guid();
      this._local.customSetItem('user', guidLog);
    }
  }

  getMaxDate(timezone: string) {
    if (timezone) {
      var offset =
        parseInt((parseInt(timezone) / 100).toFixed()) +
        (parseInt(timezone) % 100) / 60;
      var d, utc, nd;
      d = new Date();
      utc = d.getTime() + d.getTimezoneOffset() * 60000;
      nd = new Date(utc + 3600000 * offset);
      return nd;
    } else {
      return new Date();
    }
  }

  /** timezone-adjusted "now" minus a number of days — used to default date-range filters */
  getDaysAgo(days: number, timezone: string) {
    const d = this.getMaxDate(timezone);
    d.setDate(d.getDate() - days);
    return d;
  }

  updateDummyLive(): Observable<any> {
    return combineLatest([
      this.isDummy$.asObservable(),
      this.timezone$.asObservable(),
      this.selected_country$.asObservable(),
    ]).pipe(
      // Ensure it only emits when values change to avoid multiple calls
      distinctUntilChanged(
        (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
      )
    );
  }

  getTimezone() {
    let timezone: any = '';
    this.timezone$.subscribe((timeZone) => {
      timezone = timeZone;
    });
    return this.timezone;
  }

  getTimezoneName() {
    this.timezoneName$.subscribe((timeZone) => {
      this.timezoneName = timeZone;
    });
    return this.timezoneName;
  }

  getTimezoneZone() {
    let zone: any = '';
    this.zone$.subscribe((zone) => {
      zone = zone;
    });
    return zone;
  }

  getTimezoneList() {
    let timezoneList: any = '';
    this._main.timezoneList$.subscribe((timezone_list) => {
      timezoneList = timezone_list;
    });
    return timezoneList;
  }

  getAdminList(): Array<[]> {
    let admin_list: any = [];
    this._main.global_admin_list$.subscribe((adminList: any) => {
      admin_list = adminList;
    });
    return admin_list;
  }

  getGovernmentDocs(): Array<[]> {
    let gov_docs: any = [];
    this._main.government_docs$.subscribe((govDocs: any) => {
      gov_docs = govDocs;
    });
    return gov_docs;
  }

  getlanguage_data(): Observable<any> {
    return this._main.language_data$.asObservable();
  }

  getCountryData(): countryType[] {
    let countriesData: any = [];
    this._main.country_data$.subscribe((country_data: any) => {
      countriesData = country_data;
    });
    return countriesData;
  }

  getCurrencyData(): Array<[]> {
    let currenciesData: any = [];
    this._main.currency_data$.subscribe((currency_data: any) => {
      currenciesData = currency_data;
    });
    return currenciesData;
  }

  getMobileCodeArr() {
    let mobileCodeArr: any = [];
    this._main.mobile_code_arr$.subscribe((mobile_code_arr: any) => {
      mobileCodeArr = mobile_code_arr;
    });
    return mobileCodeArr;
  }

  getExactDate(d: Date) {
    return new Date(d.getTime() + d.getTimezoneOffset() * 60000);
  }

  getSalutations() {
    let salutation: any = [];
    this._main.salutations$.subscribe((salutations: any) => {
      salutation = salutations;
    });
    return salutation;
  }

  getIndustryList() {
    let industryList: any = [];
    this._main.industryList$.subscribe((industries: any) => {
      industryList = industries;
    });
    return industryList;
  }

  getStripeKey() {
    let stripeKey: any = '';
    this._main.stripeKey$.subscribe((stripe_key: any) => {
      stripeKey = stripe_key;
    });
    return stripeKey;
  }

  getCurrencySymbol(): string {
    return this._main.currencySymbol$.value || '$';
  }

  sortByFun(data: Array<[]>, key_sort_by: string): [][] {
    this.key_sort_by = key_sort_by;
    return data.sort((a: any, b: any) => a[key_sort_by] - b[key_sort_by]);
  }

  compare(a: any, b: any) {
    if (a && a[this.key_sort_by] < b && b[this.key_sort_by]) return -1;
    if (a && a[this.key_sort_by] > b && b[this.key_sort_by]) return 1;
    return 0;
  }

  getfollowupDateTimeInUTCToday(date_time: Date, timezone: string) {
    var offset =
      parseInt((parseInt(timezone) / 100).toFixed()) +
      (parseInt(timezone) % 100) / 60;
    var utc, followup_time;
    utc = new Date(date_time).getTime();
    followup_time = moment(new Date(utc - 3600000 * offset));
    return followup_time.format('YYYY-MM-DD HH:mm:00');
  }

  getfollowupDateTimeInUTC(date_time: string, timezone: any) {
    var offset =
      parseInt((parseInt(timezone) / 100).toFixed()) +
      (parseInt(timezone) % 100) / 60;
    var utc, followup_time;
    var isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      date_time = moment(date_time).format('YYYY/MM/DD HH:mm:00');
      utc = new Date(date_time.replace(/-/g, '/')).getTime();
    } else {
      utc = new Date(date_time).getTime();
    }
    followup_time = moment(new Date(utc - 3600000 * offset));

    if (isSafari) {
      return followup_time.format('YYYY/MM/DD HH:mm:00');
    }
    return followup_time.format('YYYY-MM-DD HH:mm:00');
  }

  getTransactionFee(contract: any, comparable_value: any) {
    let text = '';
    if (comparable_value < contract.threshold_amount) {
      if (contract.recurring_extra_percent) {
        text += `${contract.recurring_extra_percent.toFixed(2)}%`;
      }
      if (contract.recurring_extra_amount && contract.recurring_extra_percent) {
        text += ' + ';
      }
      if (contract.recurring_extra_amount) {
        text += `${this.getCurrencySymbol()}${contract.recurring_extra_amount.toFixed(2)}`;
      }
    } else {
      if (contract.threshold_recurring_extra_percent) {
        text += `${contract.threshold_recurring_extra_percent.toFixed(2)}%`;
      }
      if (
        contract.threshold_recurring_extra_amount &&
        contract.threshold_recurring_extra_percent
      ) {
        text += ' + ';
      }
      if (contract.threshold_recurring_extra_amount) {
        text += `${this.getCurrencySymbol()}${contract.threshold_recurring_extra_amount.toFixed(2)}`;
      }
    }
    return text;
  }

  public exportAsExcelFile(
    json1: any[],
    excelFileName: string,
    ext: string,
    col_name?: string,
    cellStyles?: { [colName: string]: { [rowIndex: number]: { bg?: string; fg?: string; bold?: boolean } } }
  ): void {
    var workbook = new ExcelJS.Workbook();
    var worksheet = workbook.addWorksheet('Sheet1'); // Add a worksheet

    const toArgb = (hex: string | undefined): string | undefined => {
      if (!hex) return undefined;
      let h = hex.trim().replace('#', '').toUpperCase();
      if (h.length === 3) {
        h = h.split('').map(c => c + c).join('');
      }
      if (h.length === 6) return 'FF' + h;
      if (h.length === 8) return h;
      return undefined;
    };

    // Assuming json1 is an array of objects where each object represents a row of data
    json1.forEach((row, rowIndex) => {
      Object.keys(row).forEach((col, colIndex) => {
        if (colIndex === 0) {
          // Set header in the first column (A)
          worksheet.getCell(rowIndex + 1, colIndex + 1).value = row[col];
        // tslint:disable-next-line:max-line-length
        } else if (col_name && col === col_name) { // Check if it's column L (12th column, assuming zero-indexed) /** new update, removed index check for make the code more dynamic*/
          const cell = worksheet.getCell(rowIndex + 1, colIndex + 1); // rowIndex + 1 to adjust for header row
          if (rowIndex == 0) {
            cell.value =  row[col]; // hyperlink: row[col] // Assuming row[col] contains the URL
            // cell.style = {
            //   font: {
            //     color: { argb: '000000' }, // Blue color (ARGB format)
            //   }
            // };
          } else {
            cell.value = {
              text: row[col],
              hyperlink: row[col] // Assuming row[col] contains the URL
            };
            cell.style = {
              font: {
                color: { argb: 'FF0000FF' }, // Blue color (ARGB format)
                underline: true,
              }
            };
          }
        } else {
          worksheet.getCell(rowIndex + 1, colIndex + 1).value = row[col]; // Set other columns normally
        }

        // Apply optional cell-level styling (e.g. priority column highlights).
        // Caller passes `cellStyles[colName][rowIndex]` with `bg`/`fg` hex.
        if (cellStyles && cellStyles[col] && cellStyles[col][rowIndex]) {
          const style = cellStyles[col][rowIndex];
          const cell = worksheet.getCell(rowIndex + 1, colIndex + 1);
          const bgArgb = toArgb(style.bg);
          const fgArgb = toArgb(style.fg);

          if (bgArgb) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: bgArgb },
            };
          }
          cell.font = {
            ...(cell.font || {}),
            color: fgArgb ? { argb: fgArgb } : (cell.font && cell.font.color),
            bold: style.bold ?? (cell.font && cell.font.bold) ?? false,
          };
          cell.alignment = {
            ...(cell.alignment || {}),
            horizontal: cell.alignment?.horizontal || 'center',
            vertical: cell.alignment?.vertical || 'middle',
          };
        }
      });
    });

    workbook.xlsx.writeBuffer().then((buffer: any = Buffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${excelFileName}${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }).catch((error: any) => {
      console.error('Error generating Excel file:', error);
    });
  }

  public exportAsMultiSheetExcelFile(
    sheets: { name: string; data: any[] }[],
    excelFileName: string
  ): void {
    const workbook = new ExcelJS.Workbook();

    sheets.forEach(({ name, data }) => {
      const worksheet = workbook.addWorksheet(this.sanitizeSheetName(name));
      data.forEach((row, rowIndex) => {
        Object.keys(row).forEach((col, colIndex) => {
          worksheet.getCell(rowIndex + 1, colIndex + 1).value = row[col];
        });
      });
    });

    workbook.xlsx.writeBuffer().then((buffer: any = Buffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${excelFileName}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }).catch((error: any) => {
      console.error('Error generating Excel file:', error);
    });
  }

  private sanitizeSheetName(name: string): string {
    return name.replace(/[\\/*?:\[\]]/g, '').substring(0, 31);
  }

  

  private saveAsExcelFile(
    buffer: any,
    fileName: string,
    extension: string
  ): void {
    console.log(fileName);
    const data: Blob = new Blob([buffer]);
    FileSaver.saveAs(data, fileName + '_export' + extension);
  }

  getClaimStatus(claim_status_id: number) {
    let claim_status_array = [
      { id: '-1', value: '-', color: '' },
      { id: '0', value: 'Not Claimed', color: '#e5b319' },
      { id: '1', value: 'Submitted', color: '#e5841c' },
      { id: '2', value: 'Conditionally Approved', color: '#4cd02e' },
      { id: '3', value: 'Denied', color: '#ee6060' },
      { id: '4', value: 'On Hold', color: '#000000' },
      { id: '5', value: 'Paid', color: '#19870e' },
      { id: '6', value: 'Need More Document(s)', color: '#ee6060' },
      { id: '7', value: 'Documents uploaded', color: '#4cd02e' },
      { id: '8', value: 'Unpaid', color: '#18a5b7' },
      { id: '9', value: 'Removed From Unpaid', color: '#ee6060' },
      { id: '10', value: 'Expired : Manually', color: '#ee6060' },
      { id: '12', value: 'Unpaid Pending', color: '#18a5b7' },
    ];
    let claim_status = claim_status_array.find((obj: any) => {
      return obj.id == claim_status_id;
    });
    return claim_status ? claim_status : '';
  }

  urlParams(data: any) {
    const body = new HttpParams({ encoder: new customQueryEncoder() })
      .set('param1', 'value1')
      .set('param2', 'value2');
    Object.keys(data).forEach((key) => {
      body.set(key, data[key]);
    });
    return body.toString();
  }

  // uploadApi(_data: any, url: any, flag: any, method?: string): Observable<any> {
  //   let api_url = this._local.customGetItem('LiveAdminBaseUrl');
  //   if (!api_url) {
  //     api_url = environment.baseUrl;
  //   }
  //   return this.http.post<any>(`${api_url}${url}`, _data).pipe(
  //     timeout(60000),
  //     map((data: any) => {
  //       if (data.flag === 377) {
  //         this._api.loginRedirect();
  //         return false;
  //       }
  //       if (data.flag === 4) {
  //         this._api.loginRedirect();
  //         if (data && data.override_text) {
  //           this._toaster.showToaster('error', data.override_text, 'error');
  //         }
  //         return false;
  //       }
  //       if (data && flag) {
  //         if (data.err) {
  //           if (data.override_text) {
  //             this.customPopups(data.override_text, 1);
  //           } else {
  //             this.customPopups(data.err, 1);
  //           }
  //         } else {
  //           this.customPopups(data.is_error, data.flag);
  //         }
  //       }
  //       return data;
  //     }),
  //     catchError((error: any) => {
  //       if (error instanceof TimeoutError) {
  //         if (!this.timeoutError) {
  //           this.timeoutError = true;
  //           this.customPopups('Request Timeout', 1);
  //         }
  //         return throwError({ error: 'Timeout Exception' });
  //       }
  //       if (error.status === 0) {
  //         if (!this.timeoutError) {
  //           this.customPopups('Unable to fetch data from server', 1);
  //         }
  //       }
  //       return throwError(error);
  //     })
  //   );
  // }

  // getZipcode(params: any) {
  //   if (params.zipcode.length > 3 && params.zipcode.length <= 6) {
  //     params['country_id'] = 1;
  //     return this.http
  //       .post<any>(
  //         `${this._local.customGetItem('LiveAdminBaseUrl')}get_zipcode`,
  //         this.urlParams(params),
  //         { headers: this._api.headers }
  //       )
  //       .pipe(
  //         map((data: any) => {
  //           return data;
  //         }),
  //         catchError((error: any) => {
  //           return throwError(error);
  //         })
  //       );
  //   }
  //   return null;
  // }

  // getZip(val: any, search_canada?: any) {
  //   return new Promise((resolve, reject) => {
  //     let params: any = { zipcode: val };
  //     if (search_canada == 1) {
  //       params['search_canada'] = 1;
  //     }
  //     this.getZipcode(params)?.subscribe(
  //       (data: any) => {
  //         resolve(data.zip_info);
  //       },
  //       (error: any) => {
  //         resolve([]);
  //       }
  //     );
  //   });
  // }
  countryWiseDataFixing(data?: any) {
    const fixing =
      data && data.toString().includes('.') ? data.toFixed(2) : data;
    return fixing;
  }

  getURLFromString(string: any): string {
    string = string.replace(/(?:\r\n|\r|\n)/g, ' <br> ');
    const tempArr = string.split(/\s+|&nbsp;/);
    tempArr.map((str: any, index: any) => {
      let tempStr = str.trim();
      if (
        tempStr.includes('https://') ||
        tempStr.includes('http://') ||
        tempStr.includes('www.')
      ) {
        if (
          tempStr.includes('www.') &&
          !tempStr.includes('https://') &&
          !tempStr.includes('http://')
        ) {
          tempStr = 'http://' + tempStr;
        }
        const newStr =
          '<a class="text-underline" href="' +
          tempStr.replace('<p>', '').replace('</p>', '') +
          '" target="_blank">' +
          tempStr +
          '</a>';
        tempArr[index] = newStr;
      } /*else if (tempStr.includes('.') && tempStr.indexOf('.') !== tempStr.length - 1) {
        const newStr = '<a class="text-underline" href="http://' + tempStr.replace('<p>', '').replace('</p>', '') + '" target="_blank">' + tempStr + '</a>';
        tempArr[index] = newStr;
      }*/
    });
    return tempArr.join(' ');
  }

  getImageType(image: any): Promise<string> {
    return new Promise((resolve) => {
      let image_type = 'png';
      if (image) {
        if (image.indexOf(';base64') > -1) {
          let image_data = image.split(';base64');
          image_data = image_data[0].split('/');
          image_type = image_data[1];
        } else {
          const image_data = image.split('.');
          image_type = image_data[image_data.length - 1];
        }
        image_type = image_type ? image_type.toLowerCase() : image_type;
        resolve(image_type);
      }
    });
  }


  playNotificationSound() {
    this.audio.play();

    setTimeout(() => {
      this.audio.pause();
    }, 20000);
  }

  stopNotificationSound() {
    this.audio.pause();
  }

  private isUploadFieldEmpty(v: any): boolean {
    return v === undefined || v === null || v === '' || (typeof v === 'string' && v.trim() === '');
  }

  private normalizeUploadValue(value: any): any {
    return this.isUploadFieldEmpty(value) ? null : value;
  }

  private resolveAccountIdFromRow(row: any): any {
    if (!row) return null;
    const aliases = ['Reference_Nbr', 'record_id', 'Account ID', 'account_id', 'reference_nbr', 'Reference Nbr'];
    for (const alias of aliases) {
      const matchKey = Object.keys(row).find(
        (key) => key.trim().toLowerCase() === alias.trim().toLowerCase()
      );
      if (matchKey && !this.isUploadFieldEmpty(row[matchKey])) {
        return row[matchKey];
      }
      if (!this.isUploadFieldEmpty(row[alias])) {
        return row[alias];
      }
    }
    return null;
  }

  private resolveRecoveryScoreFromRow(row: any): any {
    if (!row) return null;
    const aliases = ['recuvery_score', 'Recovery Score', 'recovery_score', 'Score_1', 'Score'];
    for (const alias of aliases) {
      const matchKey = Object.keys(row).find(
        (key) => key.trim().toLowerCase() === alias.trim().toLowerCase()
      );
      if (matchKey && !this.isUploadFieldEmpty(row[matchKey])) {
        return row[matchKey];
      }
      if (!this.isUploadFieldEmpty(row[alias])) {
        return row[alias];
      }
    }
    return null;
  }

  categorizeFields(data: any[]) {
    const categories: any = {
      personalInfo: [],
      addresses: [],
      bankruptcyInfo: [],
      deathInfo: [],
      phoneInfo: [],
      emailInfo: [],
      matchingScoringInfo: [],
    };
  
    data.forEach((item) => {
      const recordId = item?.record_id ?? this.resolveAccountIdFromRow(item);
      if (item && !this.isUploadFieldEmpty(recordId)) {
        const categorizedItem: any = {
          personalInfo: { record_id: recordId },
          addresses: {},
          bankruptcyInfo: {},
          deathInfo: {},
          phoneInfo: {},
          emailInfo: {},
          matchingScoringInfo: {},
        };
  
        const addressGroups: { [key: string]: any } = {};
        const phoneGroups: { [key: string]: any } = {};
        const emailGroups: { [key: string]: any } = {};
        const bankruptcyGroups: { [key: string]: any } = {};
        const recoveryScore = this.resolveRecoveryScoreFromRow(item);
        if (!this.isUploadFieldEmpty(recoveryScore)) {
          categorizedItem.matchingScoringInfo['Score'] = recoveryScore;
          categorizedItem.matchingScoringInfo['recuvery_score'] = recoveryScore;
        }
  
        Object.keys(item).forEach((key) => {
          const value = this.normalizeUploadValue(item[key]);
  
          if (["record_id", "Date of Birth", "Social Security Number", "phone", "email", "line1", "city", "state", "zip"].includes(key)) {
            if (key === 'record_id' && this.isUploadFieldEmpty(value)) {
              return;
            }

            if (key === 'email' && (this.isUploadFieldEmpty(value) || !email_regex.test(String(value).trim()))) {
              return;
            }
            categorizedItem.personalInfo[key] = value;
          } else if (key.startsWith("TloAddress")) { // for tlo address column
            const match = key.match(/^TloAddress(\d+)(\w*)/);
            if (match) {
              const addressIndex = match[1];
              const addressKey = match[2] || "full_address";
  
              if (!addressGroups[`TloAddress${addressIndex}`]) {
                addressGroups[`TloAddress${addressIndex}`] = {};
              }
  
              addressGroups[`TloAddress${addressIndex}`][addressKey] = value;
              addressGroups[`TloAddress${addressIndex}`]['contract_token'] = categorizedItem.personalInfo['record_id'];
            }
          } else if (key.toLowerCase() === "phone") { // for single phone column
            // phoneGroups["phone"] = value; // Store the standalone email column
          } else if (key.startsWith("TloPhone")) { // for tlo phone column
            const match = key.match(/^TloPhone(\d+)(\w*)/);
            if (match) {
              const phoneIndex = match[1];
              const phoneKey = match[2] || "patient_mobile";
  
              if (!phoneGroups[`TloPhone${phoneIndex}`]) {
                phoneGroups[`TloPhone${phoneIndex}`] = {};
              }
  
              phoneGroups[`TloPhone${phoneIndex}`][phoneKey] = value;
              // phoneGroups[`TloPhone${phoneIndex}`]['contract_token'] = categorizedItem.personalInfo['record_id'];
            }
          } else if (key.toLowerCase() === "email") { // for single email column
            // emailGroups["email"] = value; // Store the standalone email column
          } else  if (key.startsWith("TloBankruptcy") || key.includes('TloDateOfDeath') || key.includes('TloDeceasedFlag') || key.includes('TloReportedDeathDate') || key.includes('bob_matchcodes') || key.includes('bob_toomanyhits')) {
            if (key.includes('341')) {
              if (!bankruptcyGroups['bankruptcy341']) {
                bankruptcyGroups['bankruptcy341'] = {};
              }
              const splitKey = key.split('TloBankruptcy341');
              bankruptcyGroups['bankruptcy341'][splitKey[1]] = value;
            } else if (key.startsWith("TloBankruptcy")) {
              const match = key.match(/^TloBankruptcy([A-Za-z0-9]+?)(?=[A-Z]|\d|$)([A-Z].*)?/);
              if (match) {
                const category = match[1]; // Extracted category
                let subKey = match[2] ? match[2] : "value"; // Extract subkey
          
                // Ensure category exists in grouped object
                if (!bankruptcyGroups[category]) {
                  bankruptcyGroups[category] = {};
                }
          
                // Store the value instead of the key name
                bankruptcyGroups[category][subKey] = value;
              }
            } else if (key.includes('bob_matchcodes') || key.includes('bob_toomanyhits')) {
              bankruptcyGroups[key] = value;
            } else {
              const splitExternalKey = key.split('Tlo');
              bankruptcyGroups[splitExternalKey[1]] = value;
            }
          } else if (key.startsWith("Tlo")) { // for tlo emails
            const match = key.match(/^Tlo(\d*)Email(\w*)/);
            if (match) {
              const emailIndex = match[1] || "1"; // Default to "1" if no number is present
              const emailKey = match[2] ? match[2].toLowerCase() : "patient_email"; // Convert to lowercase for consistency

              // Skip missing/invalid alternate email so a blank cell never gets pushed as null
              
              if (emailKey === 'patient_email' && (this.isUploadFieldEmpty(value) || !email_regex.test(String(value).trim()))) {
                return;
              }

              if (!emailGroups[`TloEmail${emailIndex}`]) {
                emailGroups[`TloEmail${emailIndex}`] = {};
              }
              emailGroups[`TloEmail${emailIndex}`][emailKey] = value;
            }
          } else if (key === 'recuvery_score' || key.toLowerCase() === 'recovery score' || key.toLowerCase() === 'recovery_score') {
            categorizedItem.matchingScoringInfo['Score'] = value;
            categorizedItem.matchingScoringInfo['recuvery_score'] = value;
          } else if (key.includes("matchcodes") || key.includes("Score")) {
            categorizedItem.matchingScoringInfo[key] = value;
          }
        });

        // Drop any email group that never received a valid patient_email (e.g. only a "type" column matched)
        Object.keys(emailGroups).forEach((groupKey) => {
          if (this.isUploadFieldEmpty(emailGroups[groupKey]?.patient_email)) {
            delete emailGroups[groupKey];
          }
        });

        // Assign structured data
        categorizedItem.addresses = addressGroups;
        categorizedItem.phoneInfo = phoneGroups;
        categorizedItem.emailInfo = emailGroups;
        categorizedItem.bankruptcyInfo = bankruptcyGroups;
  
        categories.personalInfo.push(categorizedItem.personalInfo);
        if (categorizedItem.addresses && Object.keys(categorizedItem.addresses).length){
          categories.addresses.push(categorizedItem.addresses);
        } else {
          categories.addresses.push({'contract_token': recordId})
        }

        if (categorizedItem.phoneInfo && Object.keys(categorizedItem.phoneInfo).length) {
          categories.phoneInfo.push(categorizedItem.phoneInfo);
        } else {
          categories.phoneInfo.push({'contract_token': recordId})
        }

        if (categorizedItem.emailInfo && Object.keys(categorizedItem.emailInfo).length) {
          categories.emailInfo.push(categorizedItem.emailInfo);
        } else {
          categories.emailInfo.push({'contract_token': recordId})
        }

        if (categorizedItem.matchingScoringInfo && Object.keys(categorizedItem.matchingScoringInfo).length) {
          categories.matchingScoringInfo.push(categorizedItem.matchingScoringInfo);
        } else {
          categories.matchingScoringInfo.push({'contract_token': recordId})
        }

        if (categorizedItem.bankruptcyInfo && Object.keys(categorizedItem.bankruptcyInfo).length) {
          categories.bankruptcyInfo.push(categorizedItem.bankruptcyInfo);
        } else {
          categories.bankruptcyInfo.push({'contract_token': recordId})
        }
      }
    });
    return categories;
  }

  dashboardMenuExist(menuList: any) {
    return new Promise((resolve, reject) => {
      if (menuList && menuList.length) {
        const item = menuList.filter((menu: any) => (menu.menu_name + '').includes('Dashboard'));
        if (item && item.length) {
          resolve(true);
        } else {
          resolve(false);
        }
      }
    });
  }  
  categorizeOlbFields(data: any[]) {
    const flatFields = [
      'Reference_Nbr', 'Hit_Ind', 'Full_Name', 'Name_Suffix', 'SSN', 'Date_of_Birth',
      'INCOME', 'LOWEND', 'HIGHEND', 'DTIRATIO'
    ];

    const bucketMap: { [bucket: string]: string[] } = {
      currentAddress: ['Full_Address', 'City', 'State', 'Zip_Code', 'Addr_Date_Rptd'],
      prevAddress: ['Prev_Full_Address', 'Prev_City', 'Prev_State', 'Prev_Zip', 'Prev_Addr_Date_Rptd'],
      secondPrevAddress: ['2nd_Prev_Full_Address', '2nd_Prev_City', '2nd_Prev_State', '2nd_Prev_Zip'],
      phoneInfo: ['Phone_Nbr', 'Phone_Srce_Ind', 'Phone_Type', 'Phone_Nbr2', 'Phone_Srce_Ind2', 'Phone_Type2'],
      employmentInfo: ['Empl_Name', 'Empl_Occup', 'Empl_Date_Hired', 'Empl_Date_Vrfd'],
      deceasedInfo: [
        'Deceased_Flag', 'Deceased_Name', 'Deceased_City', 'Deceased_State', 'Deceased_Zip',
        'Deceased_Claim_City', 'Deceased_Claim_State', 'Deceased_Claim_Zip',
        'Deceased_Date_of_Birth', 'Deceased_Date_of_Death'
      ],
      bankruptcyInfo: [
        'Bkrpt_Flag', 'Bkrpt_Date_Rptd', 'Bkrpt_Pub_Rec_Type', 'Bkrpt_Docket_Nbr', 'Bkrpt_Attorney',
        'Bkrpt_Date_Paid', 'Bkrpt_Liab_Amt', 'Bkrpt_Acct_Desig', 'Bkrpt_Court_Type', 'Suppr/Frzn_Ind'
      ],
      score1Info: [
        'Score_Algo_Id_1', 'Score_1', 'Score_1_Indicator_Flag', 'Score_1_Derogatory_Alert',
        'Score_1_1st_Factor_Code', 'Score_1_2nd_Factor_Code', 'Score_1_3rd_Factor_Code',
        'Score_1_4th_Factor_Code', 'Score_1_Scorecard_Indicator'
      ],
      score2Info: [
        'Score_Algo_Id_2', 'Score_2', 'Score_2_Indicator_Flag', 'Score_2_Derogatory_Alert',
        'Score_2_1st_Factor_Code', 'Score_2_2nd_Factor_Code', 'Score_2_3rd_Factor_Code',
        'Score_2_4th_Factor_Code', 'Score_2_Scorecard_Indicator'
      ],
      score3Info: [
        'Score_Algo_Id_3', 'Score_3', 'Score_3_Indicator_Flag', 'Score_3_Derogatory_Alert',
        'Score_3_1st_Factor_Code', 'Score_3_2nd_Factor_Code', 'Score_3_3rd_Factor_Code',
        'Score_3_4th_Factor_Code', 'Score_3_Scorecard_Indicator'
      ],
      attrInfo: [
        'ATTR01', 'ATTR02', 'ATTR03', 'ATTR04', 'ATTR05', 'ATTR06', 'ATTR07', 'ATTR08', 'ATTR09',
        'ATTR10', 'ATTR11', 'ATTR12', 'ATTR13', 'ATTR14', 'ATTR15', 'ATTR16', 'ATTR17'
      ],
      psInfo: [
        'PS006', 'PS009', 'PS012', 'PS013', 'PS014', 'PS018', 'PS021', 'PS029', 'PS035',
        'PS036', 'PS037', 'PS038', 'PS059', 'PS062'
      ],
    };

    const isEmpty = (v: any) => this.isUploadFieldEmpty(v);

    const records: any[] = [];

    (data || []).forEach((row) => {
      const referenceNbr = !isEmpty(row?.['Reference_Nbr'])
        ? row['Reference_Nbr']
        : this.resolveAccountIdFromRow(row);

      if (!row || isEmpty(referenceNbr)) {
        return;
      }

      const record: any = {};
      const recoveryScore = this.resolveRecoveryScoreFromRow(row);

      flatFields.forEach((key) => {
        const value = key === 'Reference_Nbr' ? referenceNbr : row[key];
        record[key] = isEmpty(value) ? null : value;
      });

      Object.keys(bucketMap).forEach((bucket) => {
        const bucketObj: any = {};
        bucketMap[bucket].forEach((key) => {
          const value = row[key];
          bucketObj[key] = isEmpty(value) ? null : value;
        });
        record[bucket] = bucketObj;
      });

      if (isEmpty(record.score1Info?.['Score_1']) && !isEmpty(recoveryScore)) {
        record.score1Info['Score_1'] = recoveryScore;
      }
      record.recuvery_score = isEmpty(recoveryScore) ? null : recoveryScore;

      records.push(record);
    });

    return { records };
  }
  // Obfuscates a numeric ID for use in URLs. NOT encryption/security - XOR with a static
  // key is trivially reversible if an attacker ever learns one (encoded, plaintext) pair.
  // This only prevents casually reading/guessing sequential IDs off the URL bar.
  encodeId(id: any): string {
    if (!id) return '';
    const numId = Number(id);
    if (!Number.isFinite(numId)) return '';
    const xorValue = (numId ^ this.idXorKey) >>> 0;
    const bodyLength = this.encodedIdLength - this.idAlphaEncodingPrefix.length;
    // Configured width can't hold the value - fail closed rather than truncate/emit an
    // ambiguous ID that could collide with another encoded ID.
    if (bodyLength < this.idEncodedValueLength) {
      return '';
    }
    const scrambled = Math.imul(xorValue, this.idScrambleFactor) >>> 0;
    const encodedValue = (scrambled + this.idEncodedValueOffset).toString(this.idEncodingRadix);
    const checksum = this.idEncodingChecksum(scrambled, bodyLength - this.idEncodedValueLength);
    return `${this.idAlphaEncodingPrefix}${encodedValue}${checksum}`;
  }

  // Deterministic filler for the rest of the fixed-width body. It doubles as a cheap integrity
  // check, so a hand-edited ID is rejected outright instead of quietly decoding to another record.
  private idEncodingChecksum(value: number, length: number): string {
    if (length <= 0) return '';
    // FNV-1a over the 4 bytes of the value, seeded with the key.
    let hash = this.idXorKey >>> 0;
    for (let i = 0; i < 4; i++) {
      hash = Math.imul(hash ^ ((value >>> (i * 8)) & 0xff), 16777619) >>> 0;
    }
    return hash.toString(this.idEncodingRadix).padStart(length, '0').slice(-length);
  }

  // Format check only (prefix + charset + fixed length) - does not guarantee the value decodes
  // to a real/valid entity, just that it matches the shape one of our encoders produces.
  isEncodedRouteId(rawId: string | null | undefined): boolean {
    return isEncodedId(rawId);
  }

  // A plain, unencoded database ID: an old link or a hand-typed URL, as opposed to an encoded ID
  // that has been mangled. The two fail the same format check but mean different things to the user.
  isUnencodedRouteId(rawId: string | null | undefined): boolean {
    return isUnencodedId(rawId);
  }

  // Unencoded IDs are refused on purpose; anything else that fails the format check (truncated,
  // padded with an extra character, hand-edited) is simply a broken ID, so say so rather than
  // implying the link would have worked before the security change.
  private invalidRouteIdMessage(rawId: string | null | undefined, entityLabel: string): string {
    return this.isUnencodedRouteId(rawId)
      ? 'Invalid Id'
      : `Incorrect ${entityLabel} ID.`;
  }

  // XOR is self-inverse, so decoding is the same operation as encoding, applied to the
  // value once the prefix, the fixed offset and the checksum have been stripped off.
  decodeRouteId(rawId: string, entityLabel = 'Record'): string | null {
    if (!this.isEncodedRouteId(rawId)) {
      this.customPopups(this.invalidRouteIdMessage(rawId, entityLabel), 1);
      return null;
    }
    const normalizedId = rawId.toString().toLowerCase();
    if (!normalizedId.startsWith(this.idAlphaEncodingPrefix)) {
      return String(Number(normalizedId.slice(this.idEncodingPrefix.length)) ^ this.idXorKey);
    }
    const body = normalizedId.slice(this.idAlphaEncodingPrefix.length);
    const checksum = body.slice(this.idEncodedValueLength);
    const scrambled =
      parseInt(body.slice(0, this.idEncodedValueLength), this.idEncodingRadix) - this.idEncodedValueOffset;
    if (!Number.isInteger(scrambled) || scrambled < 0 || scrambled > 0xFFFFFFFF) {
      return null;
    }
    if (checksum !== this.idEncodingChecksum(scrambled, checksum.length)) {
      return null;
    }
    const xorValue = Math.imul(scrambled, this.idScrambleInverse) >>> 0;
    return String((xorValue ^ this.idXorKey) >>> 0);
  }

  // Route-guard convenience: validates + decodes an ID param in one call, surfacing a
  // user-facing popup at whichever step fails instead of throwing, so route components
  // can bail out early with a single null check.
  resolveRouteId(rawId: string | null | undefined, entityLabel = 'Record'): string | null {
    const normalizedId = rawId?.toString().trim();
    if (!normalizedId) {
      this.customPopups(`${entityLabel} ID is required.`, 1);
      return null;
    }
    if (!this.isEncodedRouteId(normalizedId)) {
      this.customPopups(this.invalidRouteIdMessage(normalizedId, entityLabel), 1);
      return null;
    }
    const decodedId = this.decodeRouteId(normalizedId, entityLabel);
    if (!decodedId || !/^\d+$/.test(decodedId)) {
      this.customPopups(`Incorrect ${entityLabel} ID.`, 1);
      return null;
    }
    return decodedId;
  }

  // Route-param variant of resolveRouteId: an old link or a hand-typed URL still carries the
  // plain numeric ID, so encode it, swap the address bar over to the encoded form, and validate
  // that instead. Anything already encoded (or not a plain number) is left exactly as it was and
  // still gets the usual popup + null from resolveRouteId.
  resolveRouteIdParam(rawId: string | null | undefined, entityLabel = 'Record'): string | null {
    const normalizedId = rawId?.toString().trim();
    if (normalizedId && this.isUnencodedRouteId(normalizedId)) {
      const encodedId = this.encodeId(normalizedId);
      // encodeId fails closed (returns '') when the value can't be encoded - leave the URL alone
      // in that case and let resolveRouteId report the ID as invalid.
      if (encodedId) {
        this.rewriteRouteIdInUrl(normalizedId, encodedId);
        return this.resolveRouteId(encodedId, entityLabel);
      }
    }
    return this.resolveRouteId(rawId, entityLabel);
  }

  // Replaces the raw ID segment of the current URL with its encoded form, keeping any query
  // string and fragment. replaceState is used rather than a router navigation so the history
  // entry is swapped (Back doesn't land on the raw ID again) without tearing the component down
  // or making param/queryParam subscriptions fire a second time.
  private rewriteRouteIdInUrl(rawId: string, encodedId: string): void {
    const [pathAndQuery, fragment] = this.router.url.split('#');
    const [path, query] = pathAndQuery.split('?');
    const segments = path.split('/');
    const idIndex = segments.lastIndexOf(rawId);
    if (idIndex === -1) {
      return;
    }
    segments[idIndex] = encodedId;
    this.location.replaceState(
      segments.join('/') + (query ? `?${query}` : '') + (fragment ? `#${fragment}` : '')
    );
  }

  // Silent variant of decodeRouteId: returns '' instead of null when invalid.
  decodeId(id: any): string {
    return this.decodeRouteId(id?.toString()) || '';
  }
  maskContact(value: string | string[] | null | undefined): string {
    if (value == null) {
      return '';
    }

    const profile = this._local.customGetItem('admin_profile');
    const isRestricted = profile && +profile.is_info_restricted === 1;

    if (Array.isArray(value)) {
      if (!isRestricted) {
        return value.filter((item) => item != null && (item + '').trim() !== '').join(', ');
      }
      return value
        .map((item) => this.maskContact(item))
        .filter((item) => !!item)
        .join(', ');
    }

    const input = (value + '').trim();

    if (!input) {
      return '';
    }

    if (input.includes(',')) {
      return input
        .split(',')
        .map((item) => this.maskContact(item))
        .filter((item) => !!item)
        .join(', ');
    }

    if (!isRestricted) {
      return input;
    }
    if (input.includes('@')) {
      const atIndex = input.indexOf('@');
      const localPart = input.slice(0, atIndex);
      const domain = input.slice(atIndex + 1);

      if (!localPart || !domain) {
        return input;
      }

      const firstChar = localPart.charAt(0);
      if (localPart.length <= 4) {
        return `${firstChar}****@${domain}`;
      }
      const lastThree = localPart.slice(-3);
      return `${firstChar}****${lastThree}@${domain}`;
    }

    const digits = input.replace(/\D/g, '');
    if (digits.length <= 4) {
      return input;
    }
    const lastFour = digits.slice(-4);
    const maskedPrefix = '*'.repeat(digits.length - 4);
    return `${maskedPrefix}${lastFour}`;
  }

  setNotificationCount(count: number) {
    this._notificationCount.next(count);
  }

  storeInfoAction(hash_value: any, module: any) : Observable<any> {
    const data = {
      hash_value,
      module
    }
    return this._api.api(data, 'auth/info_action_tracking');
  }
  /**
   * Masks any email or phone number embedded inside free-text — a note, a message,
   * a training description — while leaving the rest of the sentence alone. For a
   * non-restricted admin the text comes back untouched, so callers can send any
   * string through this without branching on the profile themselves.
   */
  maskContactsInText(value: string | null | undefined): string {
    if (value == null) {
      return '';
    }
    const input = String(value);
    if (!input) {
      return '';
    }

    const profile = this._local.customGetItem('admin_profile');
    const isRestricted = profile && +profile.is_info_restricted === 1;
    if (!isRestricted) {
      return input;
    }

    // emails first — the phone pattern below would otherwise latch onto digits inside an address
    const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
    let masked = input.replace(EMAIL_RE, (match) => this.maskContact(match));

    // phone — require a +1 prefix so dates like 09-15-2026(PDT) are not treated as numbers
    const PHONE_RE = /\+1(?:[\s\-().]*\d){10}/g;
    masked = masked.replace(PHONE_RE, (match) => this.maskContact(match));

    return masked;
  }

}

export enum verifyType {
  Doctor = 'Doctor',
  Partner = 'Partner',
  Patient = 'Patient',
}

export interface countryType {
  country_id: number;
  cocu_id: number;
  display_logo: '';
  country_name: '';
  country_phone_format: ''
}

export interface timezoneType {
  tz_id: number;
  name: string;
  offset: string;
  val: string
}

export interface currencyType {
  cocu_id: number;
  currency_id: number;
  currency_name: string;
  display_logo: string;
}

class customQueryEncoder implements HttpParameterCodec {
  decodeKey(key: string): string {
    throw new Error('Method not implemented.');
  }
  decodeValue(value: string): string {
    throw new Error('Method not implemented.');
  }
  encodeKey(k: string): string {
    return this.encode(k);
  }
  encodeValue(v: string): string {
    return this.encode(v);
  }

  private encode(v: string): string {
    return (
      encodeURIComponent(v)
        .replace(/%40/gi, '@')
        .replace(/%3A/gi, ':')
        .replace(/%24/gi, '$')
        .replace(/%2C/gi, ',')
        .replace(/%3B/gi, ';')
        // .replace(/%2B/gi, '+')
        .replace(/%3D/gi, '=')
        .replace(/%3F/gi, '?')
        .replace(/%2F/gi, '/')
    );
    // .replace(/%3D/gi, '/');
  }
}
export const passwordValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const password = control.value;
    if (!password) {
      return null;
    }

    const minLength = /.{6,12}/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar =/[^A-Za-z0-9]/.test(password);

    const valid = minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
    return !valid ? {
      passwordStrength: {
        requirements: `Password must be 8-12 characters long, and include at least one uppercase letter (A-Z), one lowercase letter (a-z), one number (0-9), and one special character (!@#$%^&*).`
      }
    } : null;
  };
}

// export const patterns = [
//   // (123) 456-789
//   /^\(\d{3}\)\s\d{3}-\d{4}$/,

//   // (123)-456-789
//   /^\(\d{3}\)-\d{3}-\d{4}$/,

//   // 123-456-789
//   /^\d{3}-\d{3}-\d{4}$/,

//   // (123) (456)-789
//   /^\(\d{3}\)\s\(\d{3}\)-\d{4}$/,

//   // (123)-(456)-(789)
//   /^\(\d{3}\)-\(\d{3}\)-\(\d{4}\)$/,

//   // 123456789 (no separators)
//   /^\d{10}$/,

//   // Extra tolerant: allows (123)456789
//   /^\(\d{3}\)\d{3}-?\d{4}$/,

//   // Allows mixed: (123)456-789
//   /^\(\d{3}\)\d{3}-\d{4}$/,

//   // Leading space tolerance: " (123)-456-789"
//   /^\s*\(\d{3}\)[-\s]?\d{3}-\d{4}$/
// ];

@Pipe({ name: 'phoneFormat' })
export class PhoneFormatPipe implements PipeTransform {
  transform(value: string): string {
    if (!value) return '';
    const cleaned = value.replace(/\D/g, '');
    const normalized = cleaned.length === 11 && cleaned.startsWith('1') ? cleaned.slice(1) : cleaned;
    const match = normalized.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `+1 (${match[1]}) ${match[2]}-${match[3]}` : value;
  }
}

/**
 * Country-driven phone display, using the mask configured for the country in
 * `multi_countries.country_phone_format`.
 *
 *   {{ contract.patient_mobile | countryPhone : contract.country_id }}
 *
 * Omit the country_id to use the country the panel is currently showing.
 */
@Pipe({ name: 'countryPhone' })
export class CountryPhonePipe implements PipeTransform {
  constructor(private _phone: PhoneFormatService) {}

  transform(value: any, country_id?: any): string {
    return this._phone.format(value, country_id);
  }
}

export enum Tabs {
  emails = "emails",
  phone = "phone",
  demandLetter = "demandLetter",
  message = "message"
}
export enum Status {
  Start = 'start',
  InProgress = 'in-progress',
  Completed = 'completed'
}

export enum Step {
  First = "FIRST",
  Second = "SECOND",
  Third = "THIRD",
  Fourth = "FOURTH",
  MailStep = "MAILSTEP"
}
@Pipe({ name: 'maskSensitive' })
export class MaskSensitivePipe implements PipeTransform {
  constructor(public _local: localStorageService) {}

  transform(value: string): string {
    if (!value) return value;

    const profile = this._local.customGetItem('admin_profile');
    const role = profile ? profile.role_id : 0;

    // Regex for emails
    const emailRegex = /([a-zA-Z0-9._%+-]+)@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    // Regex for phone numbers (10–15 digits)
    const phoneRegex = /\b\d{10,15}\b/g;
    if (value.includes('+1-')) {
      return value;
    }
    // ✅ If role allows viewing, just format phone numbers neatly
      return value.replace(phoneRegex, (match) => {
        if (match.length === 10) {
          return `+1 (${match.slice(0, 3)}) ${match.slice(3, 6)}-${match.slice(6)}`;
        } else if (match.length === 11 || match.length > 10) {
          return `${match.slice(0, 1)} (${match.slice(1, 4)}) ${match.slice(4, 7)}-${match.slice(7)}`;
        } else {
          return match; 
        }
      });

    // let result = value.replace(emailRegex, (match, localPart, domain) => {
    //   if (localPart.length <= 4) return `${localPart}@${domain}`;
    //   const hiddenLocalPart = ''.padEnd(localPart.length - 4, 'X') + localPart.slice(-4);
    //   return `${hiddenLocalPart}@${domain}`;
    // });

    // result = result.replace(phoneRegex, (match) => {
    //   const last4 = match.slice(-4);
    //   return `XXX-XXX-(${last4})`;
    // });

    // return result;
  }
}

@Pipe({ name: 'currencyFormat' })
export class CurrencyFormatPipe implements PipeTransform {
 transform(value: any, symbol?: string) {
    if (!value) return symbol ? `${symbol}0.00` : '0.00';
    if (typeof value === 'string' && /[^\d.,\-$\s]/.test(value)) {
      return value.replace(/\$(\d[\d,]*)(\.\d+)?/g, (match: string, whole: string, decimals: string) => {
        const digits = whole.replace(/,/g, '');
        return digits ? '$' + Number(digits).toLocaleString('en-US') + (decimals || '') : match;
      });
    }
    const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]+/g, '')) : value;
    const formatted = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
    return symbol ? `${symbol}${formatted}` : formatted;
  }
}

export function minValueValidator(min: number) {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value?.toString().replace(/,/g, '');
    const num = parseFloat(value);
    return isNaN(num) || num < min ? { minValue: { requiredMin: min } } : null;
  };
}

export function currencyFormatfunction(value: any) {
  if (!value) return '0.00';
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]+/g, '')) : value;
  return (new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num));
}
@Directive({
  selector: '[appSafeExternalLinks]'
})
export class SafeExternalLinksDirective implements AfterViewInit {
  // consent CTAs are for the customer only, so they stay read-only in admin previews
  private readonly consentHrefPattern = /consent/i;
  private readonly consentTextPattern = /agree\s+to\s+(the\s+)?electronic\s+communication/i;

  constructor(private el: ElementRef) {}

  ngAfterViewInit() {
    const links: NodeListOf<HTMLAnchorElement> = this.el.nativeElement.querySelectorAll('a');
    links.forEach(link => {
      if (this.isConsentLink(link)) {
        this.disableLink(link);
        return;
      }
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener'); // security best practice
    });
  }

  private isConsentLink(link: HTMLAnchorElement): boolean {
    const href = link.getAttribute('href') || '';
    const text = (link.textContent || '').replace(/\s+/g, ' ').trim();
    return this.consentHrefPattern.test(href) || this.consentTextPattern.test(text);
  }

  private disableLink(link: HTMLAnchorElement) {
    link.removeAttribute('href');
    link.removeAttribute('target');
    link.setAttribute('aria-disabled', 'true');
    link.style.pointerEvents = 'none';
    link.style.cursor = 'not-allowed';
    link.style.opacity = '0.65';
    link.addEventListener('click', (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
    });
  }
}
// Required fields that are typed by hand: whitespace alone must not pass as a value.
export const notBlankValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null; // leave 'is it present at all' to Validators.required
    }
    return String(value).trim().length ? null : { required: true };
  };
};
